import React, { useState, useEffect } from 'react';
import type { RoundsRacersResponse, RoundsPicksResponse } from '@/lib/collections/rounds';

const COIN_COLORS = [
  '#ff3da8', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#fb7185',
];

const LOTTERY_CAPTIONS = [
  '🎰 Shuffling the lineup...',
  "🏎️ Who's going to show up today?",
  '💎 The field is taking shape!',
  '📸 Final spots being claimed!',
  '🎯 Locking in the racers!',
  '🔥 Tension building on the grid!',
];

interface LotteryPanelProps {
  racers: RoundsRacersResponse[];
  userPick: RoundsPicksResponse | null;
  secondsLeft: number;
}

export const LotteryPanel: React.FC<LotteryPanelProps> = ({
  racers,
  userPick: _userPick,
  secondsLeft,
}) => {
  const [captionIdx, setCaptionIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setCaptionIdx((i) => (i + 1) % LOTTERY_CAPTIONS.length);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      {/* Caption ticker */}
      <div
        className="text-center text-lg font-semibold py-3 px-6 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(255,61,168,0.15), rgba(167,139,250,0.15))',
          border: '1px solid rgba(255,61,168,0.3)',
          color: '#f0abfc',
        }}
      >
        {LOTTERY_CAPTIONS[captionIdx]}
      </div>

      {/* Countdown */}
      <div className="text-center">
        <div className="text-sm font-medium mb-1" style={{ color: '#a78bfa' }}>
          Reveal in
        </div>
        <div
          className="text-5xl font-bold tabular-nums"
          style={{ color: secondsLeft <= 10 ? '#ff3da8' : '#fbbf24', fontVariantNumeric: 'tabular-nums' }}
        >
          {secondsLeft}s
        </div>
      </div>

      {/* Mystery coin corral — hidden until reveal */}
      <div
        className="relative rounded-3xl overflow-hidden p-6"
        style={{
          background: 'linear-gradient(135deg, rgba(26,10,46,0.8), rgba(15,5,30,0.95))',
          border: '2px solid rgba(167,139,250,0.3)',
          minHeight: 160,
        }}
      >
        <div className="text-center text-xs font-medium mb-4" style={{ color: '#a78bfa' }}>
          🏁 THE STARTING {racers.length > 0 ? racers.length : 5} 🏁
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {Array.from({ length: racers.length > 0 ? racers.length : 5 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center"
              style={{
                animation: `floatCoin ${2 + (i % 3) * 0.4}s ease-in-out ${i * 0.15}s infinite alternate`,
              }}
            >
              {/* Mystery placeholder card — same footprint as CoinCharacter at size=48 */}
              <div
                className="flex flex-col items-center"
                style={{ width: 68 }}
              >
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 48,
                    height: 48,
                    background: `radial-gradient(circle at 35% 35%, ${COIN_COLORS[i % COIN_COLORS.length]}33, ${COIN_COLORS[i % COIN_COLORS.length]}11)`,
                    border: `2px solid ${COIN_COLORS[i % COIN_COLORS.length]}55`,
                    boxShadow: `0 0 12px ${COIN_COLORS[i % COIN_COLORS.length]}33`,
                    fontSize: 24,
                    userSelect: 'none',
                    animation: `mysterySpin 4s ${i * 0.6}s ease-in-out infinite alternate`,
                  }}
                >
                  ?
                </div>
                <div
                  className="mt-1 text-xs font-bold tracking-widest"
                  style={{ color: `${COIN_COLORS[i % COIN_COLORS.length]}99`, letterSpacing: '0.15em' }}
                >
                  ???
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pick opens at race start note */}
      <div
        className="text-center py-3 px-5 rounded-2xl text-sm"
        style={{
          background: 'rgba(167,139,250,0.08)',
          border: '1px solid rgba(167,139,250,0.2)',
          color: '#a78bfa',
        }}
      >
        🔒 Picking opens when the race starts — stand by for the reveal!
      </div>

      <style>{`
        @keyframes floatCoin {
          from { transform: translateY(0px) rotate(-3deg); }
          to { transform: translateY(-10px) rotate(3deg); }
        }
        @keyframes coinBounce {
          from { transform: scale(1) translateY(0); }
          to { transform: scale(1.05) translateY(-4px); }
        }
        @keyframes fadeUp {
          0% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
        }
        @keyframes mysterySpin {
          0% { transform: scale(0.95) rotate(-8deg); opacity: 0.7; }
          50% { transform: scale(1.05) rotate(8deg); opacity: 1; }
          100% { transform: scale(0.95) rotate(-8deg); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};
